import React, { useRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { toast } from '@leadsnavi/ui-kit';
import LxEditor from '../editor/LxEditor';
import type { ToolbarAlwaysType } from '../editor/theme/Theme';
import CollapsibleAttachmentArea, { type CollapsibleAttachmentAreaRef } from './CollapsibleAttachmentArea';
import { filterEmailContentDom, reloadImg, type CommonUploadReq } from './utils';
import { useMailEditorStore, type AttachmentsModel, UploadStatus } from '@/stores/mailEditorStore';
import { uploadFilesPost } from '@/services/swagger/easourcing/WriteService';
import { IApiJsonResultUrlResult } from '@/services/swagger/easourcing/models';
import { createEditorInitCallback, executeWithRetry, checkEditorReady } from '../editor/utils/editorInitCallback';

interface EditorProps {
  editHeight?: number;
  onBlur?: () => void;
  onFocus?: () => void;
  onEditCreated?: (editor: MailEditorRef) => void;
  showBottomToolbar?: boolean; // 是否显示底部工具栏
  editor_default_height?: number;
  editor_max_height?: number;
  attachedStickyBottom?: boolean;
  uniqueClass?: string;
  enableContentHeightTracking?: boolean; // 是否开启内容高度跟踪
  onContentHeightChange?: (height: number) => void; // 内容高度变化回调
  onContentEditingChange?: () => void; // 正文编辑变化回调（用于跟踪编辑状态）
  customToolbarPlugin?: React.ReactNode;
  showAIWriter?: boolean; // 是否显示 AI Writer 按钮
}

export interface MailEditorRef {
  setContent: (content: string) => void;
  getContent: () => string;
  getAttachments: () => AttachmentsModel[];
  setAttachments: (attachments: AttachmentsModel[]) => void;
  getUploading: () => boolean;
  getTraceLinks: () => string[];
  getEditorInstance: () => unknown; // 获取编辑器实例
  scrollToAttachment: () => void; // 滚动到附件头部
}

// 生成一个随机id
const generateUUID = (): string => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substr(2, 5);
  return `${timestamp}${randomStr}`;
};

type uploadingFile = File & { identity?: string };

// 通用的上传
const uploadFn = (params: {
  file: File;
  relatedId?: string;
  type?: CommonUploadReq['type'];
  succFun?: (url: string) => void;
  failFun?: () => void;
}) => {
  const { file, relatedId, type, succFun, failFun } = params || {};
  uploadFilesPost({
    file: file,
    relatedId: relatedId || generateUUID(), // 暂时使用时间戳
    contentType: type || 'MAIL_ATTACHMENT',
    uploadType: type || 'MAIL_ATTACHMENT',
  })
    .then((res: IApiJsonResultUrlResult) => {
      if (succFun) succFun(res.data.url);
    })
    .catch(error => {
      console.error(error);
      if (failFun) failFun();
    });
};

// 多文件上传，并发数最多限制4
const uploadFilesFn = async (
  filesGet: File[],
  maxNum: number = 4,
  startCb: (file: File) => void, // 某个文件开始上传
  succCb: (file: File, url: string, nosKey: string) => void, // 某个文件上传成功
  failCb: (file: File) => void, // 某个文件上传失败
  allFinishCb: () => void // 所有文件上传结束
) => {
  let filesArray: File[] = [];
  let waitingFiles: File[] = [];
  const files = Array.from(filesGet);
  // 处理文件数组，根据最大并发数分配文件
  if (files && files.length > maxNum) {
    filesArray = files.slice(0, maxNum);
    waitingFiles = files.slice(maxNum);
  } else {
    filesArray = files;
    waitingFiles = [];
  }

  // 上传函数，处理单个文件的上传
  const uploadFile = async (file: File) => {
    try {
      startCb(file);
      const res = await uploadFilesPost({
        file: file,
        relatedId: '0',
        contentType: 'MAIL_ATTACHMENT',
        uploadType: 'MAIL_ATTACHMENT',
      });
      succCb(file, res.data.url, res.data.nosKey);
    } catch {
      failCb(file);
    }
    // 若有等待的文件，取出一个继续上传
    if (waitingFiles.length > 0) {
      const nextFile = waitingFiles.shift()!;
      await uploadFile(nextFile);
    }
  };

  // 启动并发上传
  const uploadPromises = filesArray.map(uploadFile);
  await Promise.all(uploadPromises);
  if (allFinishCb) allFinishCb();
};

const MailEditor = React.forwardRef((props: EditorProps, ref: React.ForwardedRef<MailEditorRef>) => {
  const {
    onBlur,
    onFocus,
    onEditCreated,
    showBottomToolbar: _showBottomToolbar = false,
    attachedStickyBottom = false,
    uniqueClass = 'mail-editor',
    enableContentHeightTracking = false,
    onContentHeightChange,
    onContentEditingChange,
    customToolbarPlugin,
    showAIWriter = false,
  } = props;

  // 使用 store 管理状态
  const {
    content,
    quotedContent,
    attachments,
    uploading,
    setContent: setStoreContent,
    setAttachments,
    addAttachment,
    removeAttachment,
    updateAttachment,
    setUploading,
    getSuccessAttachments,
    appendQuotedContentToEditor,
  } = useMailEditorStore();

  // 获取保存草稿的确认函数

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const mailEdOut = useRef<HTMLDivElement>(null);
  const attachmentAreaRef = useRef<CollapsibleAttachmentAreaRef>(null);
  const [delaySetSuc, setDelaySetSuc] = useState(false);
  const [delaySet, setDelaySet] = useState(false);
  // 创建一个内部 ref 来存储 MailEditorRef，用于在 setup 函数中访问
  const mailEditorRefInstance = useRef<MailEditorRef | null>(null);
  useImperativeHandle(ref, () => {
    const mailEditorRef: MailEditorRef = {
      setContent(_content: string) {
        console.log('[MailEditor] setContent', 'setContent', _content);
        const filteredContent = _content; //filterEmailContentDom(_content);

        if (!editorRef.current) {
          console.error('[MailEditor] setContent', 'editorRef.current is null', editorRef.current);
          setStoreContent(filteredContent);
          setDelaySet(true);
          return;
        }

        // 使用重试机制设置内容
        executeWithRetry(
          () => {
            const editor = getReadyEditor();
            if (editor) {
              try {
                // 编辑器已准备好，设置内容
                console.log('[MailEditor] setContent', 'Editor ready, setting content');
                editor.setContent(filteredContent);

                // 设置内容后，检查编辑器实际内容是否为空，如果不为空则清除 placeholder
                setTimeout(() => {
                  const currentEditor = getReadyEditor();
                  if (currentEditor) {
                    const body = currentEditor.getBody();
                    if (body) {
                      // 检查内容是否为空：textContent 可以正确获取文本内容（包括表情等 Unicode 字符）
                      const textContent = body.textContent || '';
                      // 如果内容不为空，则清除 placeholder；如果为空，则显示 placeholder
                      const value = textContent.trim().length > 0 ? null : 'Write your email content...';
                      console.log('[MailEditor] setContent', 'Editor ready, setting placeHolder', value);
                      currentEditor.fire('setPlaceHolder', { value });
                    }
                  }
                }, 200);
                return true;
              } catch (e) {
                console.error('[MailEditor] setContent', 'Error setting content', e);
                return false;
              }
            }
            return false;
          },
          {
            maxRetries: 20,
            retryDelay: 50,
            onRetry: (retryCount, maxRetries) => {
              console.log(
                '[MailEditor] setContent',
                `Editor not ready, retrying setContent... (${retryCount}/${maxRetries})`
              );
            },
            onFailure: () => {
              // 即使检查失败也尝试设置内容（避免无限等待）
              console.error('[MailEditor] setContent', 'Editor not ready after max retries, setting content anyway');
              try {
                editorRef.current?.setContent(filteredContent);
              } catch (e) {
                console.error('[MailEditor] setContent', 'Failed to set content after max retries', e);
              }
            },
          }
        );
      },
      getContent() {
        return editorRef.current?.getContent() || '';
      },
      getAttachments() {
        return getSuccessAttachments();
      },
      setAttachments(attachments: AttachmentsModel[]) {
        return setAttachments(attachments);
      },
      getUploading() {
        return !!uploading;
      },
      getTraceLinks() {
        const body = editorRef.current?.getBody();
        const ret: { [key: string]: number } = {};
        if (body) {
          const links = Array.from(body.querySelectorAll('a'));
          const excludeLinks = Array.from(
            body.querySelectorAll(
              '.mail-signature a, .sirius-product-table a, .sirius-landing-page-table a, .sirius-site-link-button'
            )
          ) as unknown as HTMLAnchorElement[];
          links.forEach(a => {
            const anchor = a as HTMLAnchorElement;
            const href = anchor.getAttribute('href') || ''; // a.href会自动在url上加path
            if (
              excludeLinks.includes(anchor) ||
              anchor.classList.contains('gator-unsubscribe') ||
              /^https:\/\/sirius-it-edm\.cowork\.netease\.com\/unsubscribe(?:[_\w]?)\.html/.test(href)
            ) {
              // 退订链接
              return;
            }
            // 排除签名中链接
            if (href && /^https?:\/\/.+/.test(href) /*&& (a.closest('.mail-signature') === null)) */) {
              ret[href] = 1;
            }
          });
        }
        return Object.keys(ret);
      },
      getEditorInstance() {
        return editorRef.current;
      },
      scrollToAttachment() {
        if (attachmentAreaRef.current) {
          attachmentAreaRef.current.scrollToAttachment();
        }
      },
    };
    // 将 MailEditorRef 存储到内部 ref 中，以便在 setup 函数中访问
    mailEditorRefInstance.current = mailEditorRef;
    return mailEditorRef;
  });

  useEffect(() => {
    if (delaySetSuc) return;
    if (delaySet && editorRef && content) {
      setDelaySetSuc(true);
      setTimeout(() => {
        editorRef?.current?.setContent(content);
      }, 1000);
    }
  }, [delaySet, editorRef, content, delaySetSuc]);

  const onEditorChange = () => {
    // 通知上层组件内容正在编辑
    if (onContentEditingChange) {
      onContentEditingChange();
    }
  };

  /**
   * 获取编辑器实例（如果已准备好）
   * @returns 编辑器实例或 null
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getReadyEditor = useCallback((): any | null => {
    const editor = editorRef.current;
    if (editor && checkEditorReady(editor)) {
      return editor;
    }
    return null;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setup = (editor: any) => {
    console.log('setup', editor);
    editor.on('blur', () => {
      if (onBlur) onBlur();
    });
    editor.on('focusin', () => {
      if (onFocus) onFocus();
    });
    editorRef.current = editor;
    // 不在 setup 中调用 onEditCreated，因为此时编辑器可能还没有完全初始化
    // 改为在 init_instance_callback 中调用，确保编辑器完全准备好
  };

  // 编辑器完全初始化后的回调
  const initInstanceCallback = createEditorInitCallback({
    editorRef,
    componentRefInstance: mailEditorRefInstance,
    onEditCreated,
  });

  // 上传附件
  const attachmentUploadHandler = (files: File[]) => {
    if (files && files.length) {
      // 拦截附件总大小不能超过10M
      const filesGet = Array.from(files);
      let totalSize = 0; // 附件总大小
      // 当前现有的附件的总大小
      attachments.forEach(attachment => {
        if (attachment.status !== 'failed') {
          totalSize += attachment.fileSize;
        }
      });
      const maxSize = 1024 * 1024 * 19; // 附件总大小的上限19M
      const singleMaxSize = 1024 * 1024 * 10; // 单个附件大小的上限10M
      let singleOver = false;
      filesGet.forEach(f => {
        totalSize += f.size;
        if (f.size >= singleMaxSize) {
          singleOver = true;
        }
      });
      if (singleOver) {
        toast.error('The maximum size of one attachment must not exceed 10MB');
        return;
      }
      if (totalSize >= maxSize) {
        toast.error('The total size of all attachments in a single email must not exceed 19MB.');
        return;
      }
      setUploading(true);
      setTimeout(() => {
        const el = document.querySelector('.scrollBox');
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      }, 100);

      // 处理新附件
      const newAttachments = filesGet.filter(
        file =>
          !(file as uploadingFile).identity ||
          !attachments.some(attachment => attachment.identity === (file as uploadingFile).identity)
      );

      // 整理格式并添加到 store
      const newFilesWaiting = newAttachments.map(file => {
        const { type, size, name, identity } = file as uploadingFile;
        const identityNew = identity || generateUUID();
        (file as uploadingFile).identity = identityNew;
        return {
          fileName: name,
          fileSize: size,
          type,
          downloadUrl: '',
          identity: identityNew,
          status: 'waiting' as UploadStatus,
        } as AttachmentsModel;
      });

      // 更新现有附件状态
      attachments.forEach(attachment => {
        const shouldWait = filesGet.some(
          file => (file as uploadingFile).identity && attachment.identity === (file as uploadingFile).identity
        );
        if (shouldWait) {
          updateAttachment(attachment.identity!, { status: 'waiting' });
        }
      });

      // 添加新附件
      newFilesWaiting.forEach(attachment => {
        addAttachment(attachment);
      });

      // 开始上传
      uploadFilesFn(
        files,
        4,
        (file: File) => {
          const { identity } = file as uploadingFile;
          updateAttachment(identity!, { status: 'uploading' });
        },
        // 成功
        (file: File, url: string, nosKey: string) => {
          const { identity } = file as uploadingFile;
          updateAttachment(identity!, {
            downloadUrl: url,
            nosKey: nosKey,
            status: 'success',
          });
        },
        // 失败
        (file: File) => {
          const { identity } = file as uploadingFile;
          updateAttachment(identity!, {
            originFile: file,
            status: 'failed',
          });
        },
        // 全部完成
        () => setUploading(false)
      );
    }
  };

  // 上传图片
  const imagesUploadHandler = (
    blobInfo: { blob: () => File },
    succFun: (arg0: { url: string; originUrl: string }) => void,
    failFun: () => void
  ) => {
    editorRef.current?.fire('setPlaceHolder', { value: null });
    const file = blobInfo.blob() as File;
    setUploading(true);
    uploadFn({
      file,
      relatedId: '0', //generateUUID(),
      type: 'MAIL_PICS',
      succFun: url => {
        setUploading(false);
        succFun({ url, originUrl: url });
      },
      failFun: () => {
        setUploading(false);
        failFun();
      },
    });
  };

  // 删除附件
  const handleDeleteAttachment = (identity: string) => {
    removeAttachment(identity);
  };

  // 重新上传
  const handleReuploadAttachment = (identity: string) => {
    const file = attachments.find(attachment => attachment.identity === identity)?.originFile;
    console.log(4444, file);
    if (file) attachmentUploadHandler([file]);
  };

  const errorImgAction = (imgEle: HTMLImageElement) => {
    console.log('imgEle', imgEle.src);
    reloadImg([imgEle.src]).then((res: { [key: string]: string }) => {
      if (res[imgEle.src]) {
        imgEle.src = res[imgEle.src];
      } else {
        console.error('img reload failed:', imgEle.src);
      }
    });
  };

  // 编辑器配置
  const init = {
    allowedFileTypes: [
      '.jpeg',
      '.gif',
      '.png',
      '.jpg',
      '.svg',
      '.webp',
      '.tiff',
      '.ico',
      '.pdf',
      '.doc',
      '.docx',
      '.eml',
      '.xls',
      '.xlsx',
      '.ppt',
      '.pptx',
      '.zip',
      '.rar',
      '.7z',
      '.txt',
      '.csv',
      '.mp3',
      '.mp4',
      '.html',
      '.json',
      '.rtf',
      '.xmind',
      '.key',
    ],
    setup: setup,
    init_instance_callback: initInstanceCallback,
    plugins: [
      'advlist autolink lists link autoresize lximg image print fullpage media table paste code',
      'searchreplace visualblocks fullscreen nonbreaking lxinputdirection',
      'lxmailformat wordcount lxemoji lxformatpainter',
    ],
    selector: 'textarea', // change this value according to your HTML
    toolbar_mode: 'floating',
    min_height: 100,
    images_upload_handler: imagesUploadHandler,
    attachment_upload_handler: attachmentUploadHandler,
    error_img_action: errorImgAction,
    placeholder: 'Write your email content...',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paste_preprocess: (_: unknown, args: any) => {
      console.log('paste_preprocess', args);
      args.force = true;
      args.content = filterEmailContentDom(args.content);
    },
    attachedStickyBottom: attachedStickyBottom,
    className: 'items-end justify-between',
    enableDraggingAttachmentUpload: true,
  };

  // 根据是否显示底部工具栏配置不同的toolbar
  const toolbarConfig: {
    toolbarAlways: ToolbarAlwaysType;
    toolbarExtendA?: ToolbarAlwaysType;
    toolbarExtend?: ToolbarAlwaysType;
    toolbarAutoExtend?: ToolbarAlwaysType;
    toolbarAutoExtendRow2?: ToolbarAlwaysType;
  } = {
    // 窗口模式：使用原有的工具栏配置 + 自动扩展工具栏
    // 当窗口最小宽度 525px 时，bold/italic/ullists/ollists 会移入 autoExtend 浮层
    toolbarAlways: [
      ...(showAIWriter ? (['aiWriter'] as ToolbarAlwaysType) : []),
      'undo',
      'redo',
      'autoExtend',
      'split',
      'emoji',
      'link',
      'attachment',
      'uploadImg',
    ] as ToolbarAlwaysType,
    // 自动扩展工具栏第一行（浮层底部，靠近主工具栏）：
    // fontselect, fontsizeselect, bold, italic, underline, strikethrough, fontcolor, backcolor
    toolbarAutoExtend: [
      'split',
      'fontselect',
      'fontsizeselect',
      'split',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'fontcolor',
      'backcolor',
    ] as ToolbarAlwaysType,
    // 自动扩展工具栏第二行（浮层顶部，远离主工具栏）：
    // alignItems, lineheight, outdent, indent, ollists, ullists
    toolbarAutoExtendRow2: ['alignItems', 'lineheight', 'outdent', 'indent', 'ollists', 'ullists'] as ToolbarAlwaysType,
  };

  return (
    <div ref={mailEdOut} className="h-full">
      <LxEditor
        initialValue={content}
        toolbarAlways={toolbarConfig.toolbarAlways}
        toolbarExtendA={toolbarConfig.toolbarExtendA}
        toolbarExtend={toolbarConfig.toolbarExtend}
        toolbarAutoExtend={toolbarConfig.toolbarAutoExtend}
        toolbarAutoExtendRow2={toolbarConfig.toolbarAutoExtendRow2}
        uniqueClass={uniqueClass}
        init={init}
        onEditorChange={debounce(onEditorChange, 1000)}
        enableContentHeightTracking={enableContentHeightTracking}
        onContentHeightChange={onContentHeightChange}
        customToolbarPlugin={customToolbarPlugin}
        showQuotedDot={!!quotedContent}
        quotedContentAction={() => {
          appendQuotedContentToEditor(editorRef);
        }}
      >
        <>
          {attachments.length > 0 && (
            <CollapsibleAttachmentArea
              ref={attachmentAreaRef}
              attachments={attachments}
              onDeleteAttachment={handleDeleteAttachment}
              onReuploadAttachment={handleReuploadAttachment}
            />
          )}
        </>
      </LxEditor>
    </div>
  );
});

export default MailEditor;

// 导出全局邮件编辑器相关组件和服务
export { default as GlobalCampaignMailEditorDialog } from './global-campaign-mail-editor-dialog';
export { default as GlobalCampaignMailEditorService } from './global-campaign-mail-editor-dialog/service';
export { default as GlobalMailEditorModal } from './global-mail-editor-modal';
export { default as GlobalMailEditorService } from './global-mail-editor-modal/service';

// 导出类型
export type { CampaignMailEditorData } from './global-campaign-mail-editor-dialog';
export type { GlobalCampaignMailEditorOptions } from './global-campaign-mail-editor-dialog/service';
export type { GlobalMailEditorOptions } from './global-mail-editor-modal/service';
